import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import React from "react";
import uploadFiles from "../../../../lib/uploadFiles";
import StateInterface from "../../../../redux-magic/state-interface";
import { sendChatMessageThunk } from "../../../../redux-magic/store";

const StackContainer = styled("div")({
	display: "flex",
	flexWrap: "wrap",
	gap: "2px",
	maxWidth: "280px", // Instagram-like width
	borderRadius: "16px",
	overflow: "hidden",
});

const ProgressContainer = styled("div")({
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	zIndex: 2,
});

const ImageWrapper = styled("div")({
	position: "relative",
	width: "100%",
	height: "100%",
});
const GridImage = styled("img")({
	objectFit: "cover",
	borderRadius: "0",
	"&.single": {
		width: "180px",
		height: "180px",
	},
	"&.double": {
		width: "139px",
		height: "180px",
	},
	"&.triple": {
		"&.large": {
			width: "280px",
			height: "186px",
		},
		"&.small": {
			width: "139px",
			height: "92px",
		},
	},
	"&.quad": {
		width: "139px",
		height: "139px",
	},
});

const MessageText = styled("div")({
	padding: "8px 12px",
	fontSize: "14px",
	color: "#333",
	width: "100%",
	wordBreak: "break-word",
	maxWidth: "280px",
});
interface UploadProgress {
	fileId: ObjectId | null;
	percentage: number;
	stage: string;
}

interface FileAndMore {
	file: File;
	mime_type: string;
}
const ImageStackUploadPreview = ({
	images,
	session,
	dispatch,
	router,
	profile_context,
	initial_load_done,
	messages_loading,
	chat_id,
	pending_uploads,
	current_message,
	setCurrentMessage,
	sendFiles,
	setSendFiles,
	setSelectedFilePreview,
	setPendingUploads,
}: {
	images: string[];
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	initial_load_done: boolean;
	messages_loading: boolean;
	chat_id: string;
	pending_uploads: Array<FileAndMore>;
	current_message: string;
	sendFiles: boolean;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<FileAndMore>>>;
}) => {
	const extraCount = images.length - 2;

	const [selectedFiles, setSelectedFiles] = React.useState<Array<FileAndMore>>([]);

	const [percentage, setPercentage] = React.useState<Array<number>>([0]);

	const [stage, setStage] = React.useState<Array<string>>(["NOT_STARTED"]);

	const allFilesFetched = stage.every((status) => status === "FETCHED_FILE_INFORMATION");

	const [fileId, setFileId] = React.useState<Array<ObjectId | null>>([null]);

	const [load, setLoad] = React.useState<boolean>(false);

	const isLoading = stage.some(
		(s) => s === undefined || s === "NOT_STARTED" || (s && s !== "FETCHED_FILE_INFORMATION"),
	);

	const handleUpload = async (validFiles: Array<FileAndMore>, filePreview: Array<string>) => {
		setLoad(true);
		await uploadFiles(
			session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
			validFiles.map((file: FileAndMore) => ({
				file: file.file,
				mime_type: file.mime_type,
				original_file_id: new ObjectId(),
				original_file_server_path: "",
				original: true,
				is_business_profile: profile_context ? profile_context.is_business_profile : false,
				user_id: profile_context ? new ObjectId(profile_context.user_id as string) : new ObjectId(),
				business_profile_id: profile_context
					? new ObjectId(profile_context.business_profile_id as string)
					: new ObjectId(),
				entity: "business_profile",
				entity_id: chat_id ? new ObjectId(chat_id) : new ObjectId(),
				use_case: "chat_image",
				uiPath: "/",
				isPublic: false,
				text: "chat_image",
			})),
			(uploadProgress: Array<UploadProgress>) => {
				const data = uploadProgress.map((progress) => progress.fileId);

				const uploadedProg = uploadProgress.map((progress) => progress);

				const stageInfo = uploadProgress.map((progress) => progress.stage);

				const percentageInfo = uploadProgress.map((progress) => progress.percentage);

				fileId[0] != null ? setFileId([...fileId, ...data]) : setFileId(data);

				percentage[0] != 0 ? setPercentage([...percentage, ...percentageInfo]) : setPercentage(percentageInfo);

				const allFetched = uploadedProg.every((progress) => progress.stage === "FETCHED_FILE_INFORMATION");

				if (allFetched) {
					stage[0] != "NOT_STARTED" ? setStage([...stage, ...stageInfo]) : setStage(stageInfo);
				}
			},
		);
		setLoad(false);
	};


	React.useEffect(() => {
		if (fileId && fileId.length > 0 && allFilesFetched) {
			const filesToUpload = fileId.map((id, index) => ({
				file_id: id ? id.toString() : "",
				mime_type: selectedFiles[index]?.mime_type || "",
			}));

			dispatch(
				sendChatMessageThunk({
					chat_id: chat_id,
					sender_is_business_profile: profile_context.is_business_profile,
					sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					sender_business_profile_id: profile_context.business_profile_id,
					message_body: current_message,
					attachments: filesToUpload,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			);
			setSelectedFiles([]);
			setFileId([null]);
			setPercentage([0]);
			setStage(["NOT_STARTED"]);
			setCurrentMessage("");
			setSendFiles(false);
			setSelectedFilePreview([]);
			setPendingUploads([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileId, allFilesFetched]);

	React.useEffect(() => {
		if (pending_uploads && pending_uploads.length > 0 && images && images.length > 0 && sendFiles) {
			const validFiles = pending_uploads
				.filter((fileAndMore) => fileAndMore.file && fileAndMore.mime_type)
				.map((fileAndMore) => ({
					file: fileAndMore.file,
					mime_type: fileAndMore.mime_type,
				}));

			if (validFiles.length > 0) {
				handleUpload(validFiles, images);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pending_uploads, images, sendFiles]);

	const renderImageGrid = () => {
		switch (images.length) {
			case 1:
				return (
					<StackContainer>
						<GridImage
							src={images[0]}
							alt="preview-0"
							className="single"
						/>
					</StackContainer>
				);

			case 2:
				return (
					<StackContainer>
						{images.map((src, index) => (
							<GridImage
								key={index}
								src={src}
								alt={`preview-${index}`}
								className="double"
							/>
						))}
					</StackContainer>
				);

			case 3:
				return (
					<StackContainer>
						<GridImage
							src={images[0]}
							alt="preview-0"
							className="triple large"
						/>
						<div style={{ display: "flex", gap: "2px" }}>
							{images.slice(1).map((src, index) => (
								<GridImage
									key={index + 1}
									src={src}
									alt={`preview-${index + 1}`}
									className="triple small"
								/>
							))}
						</div>
					</StackContainer>
				);

			default:
				return (
					<StackContainer>
						<div style={{ display: "flex", gap: "2px" }}>
							<GridImage
								src={images[0]}
								alt="preview-0"
								className="quad"
							/>
							<GridImage
								src={images[1]}
								alt="preview-1"
								className="quad"
							/>
						</div>
						<div style={{ display: "flex", gap: "2px" }}>
							<GridImage
								src={images[2]}
								alt="preview-2"
								className="quad"
							/>
							<ImageWrapper
								sx={{
									position: "relative",
									width: "139px",
									height: "139px",
									overflow: "hidden",
								}}
							>
								<GridImage
									src={images[3]}
									alt="preview-3"
									className="quad"
								/>
								{images.length > 4 && (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											background: "rgba(0,0,0,0.5)",
											color: "white",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: "16px",
											fontWeight: "bold",
										}}
									>
										+{images.length - 4}
									</div>
								)}
							</ImageWrapper>
						</div>
					</StackContainer>
				);
		}
	};

	return (
		<div style={{ position: "relative" }}>
			{isLoading && (
				<ProgressContainer
					sx={{
						position: "absolute",
						width: "100%",
						height: "100%",
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						zIndex: 3,
						borderRadius: "16px",
					}}
				>
					<CircularProgress sx={{ color: "#FFF" }} />
				</ProgressContainer>
			)}
			{renderImageGrid()}
			{current_message && <MessageText>{current_message}</MessageText>}
		</div>
	);
};

export default ImageStackUploadPreview;
